// Training Data - Split into Personal and Business Training paths

// ==========================================
// 1. PERSONAL TRAINING DATA
// ==========================================
export const PersonalTrainingData = [
  {
    id: "professional-training",
    title: "Professional Training",
    shortDescription: "Accredited qualifications for career progression",
    description: "Industry-recognized, accredited qualifications that open doors to employment and career progression in growing sectors.",
    category: "professional",
    whoThisHelps: [
      "Career changers",
      "Job seekers needing accredited credentials",
      "Those looking to advance in their current roles",
      "Individuals entering the workforce"
    ],
    programs: [
      {
        id: "customer-service",
        name: "Customer Service Level 2/3",
        level: "Level 2 & 3",
        duration: "12-20 weeks",
        shortDescription: "Essential skills for customer-facing roles",
        fullDescription: "A comprehensive qualification designed to equip learners with the essential skills, knowledge, and behaviours required for success in customer-facing roles across all sectors. This course combines theoretical understanding with practical application, preparing learners to deliver exceptional customer experiences, resolve complex issues professionally, and contribute positively to their organisation's service standards. Suitable for those new to customer service or seeking to formalise their experience with a recognised qualification.",
        topics: [
          "Customer communication",
          "Problem resolution",
          "Service delivery",
          "Team collaboration"
        ],
        modules: [
          {
            id: "module-1",
            title: "Customer Communication",
            level: "Level 2 & 3",
            description: "Master the art of effective communication with customers across all channels",
            learningOutcomes: [
              "Demonstrate active listening techniques in customer interactions",
              "Adapt communication style to suit different customer needs and situations",
              "Communicate clearly and professionally in person, by phone, and in writing",
              "Use appropriate body language and non-verbal communication",
              "Handle difficult conversations with empathy and professionalism",
              "Understand and respond to cultural differences in communication"
            ],
            topics: [
              "Active listening and questioning techniques",
              "Verbal and non-verbal communication",
              "Written communication for customer service",
              "Telephone and email etiquette",
              "Adapting communication for different audiences",
              "Managing emotional conversations",
              "Cross-cultural communication awareness"
            ],
            assessmentCriteria: [
              "Observe and reflect on customer interactions",
              "Complete communication role-play scenarios",
              "Produce written customer correspondence samples",
              "Demonstrate telephone techniques in simulated calls"
            ]
          },
          {
            id: "module-2",
            title: "Problem Resolution",
            level: "Level 2 & 3",
            description: "Develop skills to handle complaints, resolve conflicts, and turn negative experiences into positive outcomes",
            learningOutcomes: [
              "Identify and categorise different types of customer problems and complaints",
              "Apply systematic problem-solving approaches to customer issues",
              "De-escalate conflict situations calmly and professionally",
              "Turn complaints into opportunities to build customer loyalty",
              "Know when and how to escalate issues appropriately",
              "Implement service recovery techniques effectively",
              "Document and learn from resolved issues"
            ],
            topics: [
              "Understanding customer complaints and their causes",
              "Problem-solving frameworks and techniques",
              "Conflict de-escalation strategies",
              "Empathy and emotional intelligence in problem resolution",
              "Service recovery and turning negatives into positives",
              "Escalation procedures and when to involve supervisors",
              "Learning from feedback and complaints",
              "Legal and regulatory considerations"
            ],
            assessmentCriteria: [
              "Analyse case studies of customer complaints",
              "Demonstrate problem resolution through role-play",
              "Create a service recovery plan",
              "Complete a reflective journal on handling difficult situations"
            ]
          },
          {
            id: "module-3",
            title: "Service Delivery",
            level: "Level 2 & 3",
            description: "Understand and deliver excellent service that meets and exceeds customer expectations",
            learningOutcomes: [
              "Understand what constitutes excellent customer service",
              "Identify and respond to diverse customer needs and expectations",
              "Deliver consistent service that meets organisational standards",
              "Go beyond basic expectations to create memorable experiences",
              "Work within organisational policies and procedures",
              "Use customer feedback to improve service delivery",
              "Manage time and prioritise effectively in service roles"
            ],
            topics: [
              "Principles of excellent customer service",
              "Understanding customer needs and expectations",
              "Organisational service standards and policies",
              "Exceeding expectations and adding value",
              "Managing customer expectations realistically",
              "Handling high-volume service periods",
              "Using customer feedback constructively",
              "Quality assurance in service delivery",
              "Multi-tasking and prioritisation"
            ],
            assessmentCriteria: [
              "Evaluate service delivery in a real or simulated environment",
              "Create a customer journey map identifying touchpoints",
              "Develop suggestions for service improvement",
              "Demonstrate service delivery in practical scenarios"
            ]
          },
          {
            id: "module-4",
            title: "Team Collaboration",
            level: "Level 2 & 3",
            description: "Work effectively with colleagues to deliver seamless customer experiences",
            learningOutcomes: [
              "Understand the importance of teamwork in customer service",
              "Communicate effectively with team members about customer needs",
              "Support colleagues during busy periods and challenging situations",
              "Share knowledge and best practices within the team",
              "Contribute positively to team goals and objectives",
              "Handle handovers and transitions smoothly",
              "Recognise and celebrate team achievements"
            ],
            topics: [
              "The role of teamwork in customer service excellence",
              "Effective team communication",
              "Supporting colleagues and sharing workload",
              "Knowledge sharing and continuous improvement",
              "Handover procedures and continuity of service",
              "Working with other departments",
              "Team goals and collective responsibility",
              "Building positive team relationships"
            ],
            assessmentCriteria: [
              "Participate in team-based problem-solving exercises",
              "Demonstrate effective handover communication",
              "Contribute to a team project or initiative",
              "Reflect on personal contribution to team success"
            ]
          }
        ],
        learningOutcomes: [
          "Communicate professionally and effectively with customers across all channels",
          "Handle complaints and difficult situations with confidence and empathy",
          "Deliver service that meets and exceeds customer expectations",
          "Work collaboratively with colleagues to ensure seamless customer experiences",
          "Understand and apply organisational service standards and policies",
          "Use feedback and reflection to continuously improve service delivery"
        ],
        assessmentMethods: [
          "Practical observations in real or simulated environments",
          "Portfolio of evidence demonstrating competency",
          "Written assignments and reflective journals",
          "Role-play scenarios and case study analysis",
          "Knowledge tests and professional discussions"
        ],
        entryRequirements: [
          "Age 19+ (funding eligibility)",
          "No formal qualifications required for Level 2",
          "Level 2 Customer Service or relevant experience for Level 3",
          "Good standard of written and spoken English",
          "Commitment to attend sessions and complete coursework"
        ],
        whatYouWillLearn: "You will develop the core skills needed to thrive in any customer-facing role. From mastering professional communication to handling the most challenging customer situations, this course gives you practical, immediately applicable skills that employers value. You'll learn through a mix of theory, practical exercises, real-world scenarios, and reflective practice.",
        progressionOpportunities: [
          "Level 3/4 qualifications in Customer Service or Business",
          "Team Leader or Supervisor roles",
          "Specialised customer service roles (complaints handler, customer success)",
          "Apprenticeships in retail, hospitality, or business administration",
          "Employment in customer-facing positions across all sectors"
        ],
        careerPaths: [
          {
            title: "Retail",
            roles: "Shop assistant, customer advisor, sales associate, retail supervisor"
          },
          {
            title: "Hospitality",
            roles: "Front desk receptionist, hotel guest services, restaurant host, events coordinator"
          },
          {
            title: "Call Centers",
            roles: "Customer service advisor, technical support, sales representative, team leader"
          },
          {
            title: "Reception",
            roles: "Office receptionist, medical receptionist, front of house, administrative assistant"
          }
        ],
        whoThisCourseIsFor: [
          "School leavers entering the workforce",
          "Career changers moving into customer-facing roles",
          "Current customer service staff seeking formal qualification",
          "Those returning to work after a career break",
          "Anyone wanting to improve their employability skills"
        ],
        courseBenefits: [
          "Nationally recognised qualification",
          "Practical skills you can apply immediately",
          "Improved confidence in customer interactions",
          "Enhanced employability across multiple sectors",
          "Foundation for career progression",
          "Funding available for eligible learners"
        ],
        employmentOutcomes: "Retail, hospitality, call centers, reception",
        deliveryMethods: [
          "Classroom-based sessions",
          "Online learning modules",
          "Blended learning options available",
          "Workplace-based assessment (where applicable)"
        ],
        accreditation: "Nationally recognised qualification - NCFE/City & Guilds equivalent",
        successStory: {
          name: "Priya",
          background: "Career changer from hospitality",
          journey: "Completed Level 2 Customer Service while working part-time. Gained confidence and formal qualification. Now works as a customer success manager at a tech company.",
          quote: "This course gave me the language and framework to understand what I was already doing well, and the skills to handle situations I used to find stressful. It transformed my career prospects."
        }
      },
      {
        id: "health-safety",
        name: "Health & Safety Training",
        level: "Level 2",
        duration: "1-2 days",
        shortDescription: "Workplace safety compliance and best practices",
        fullDescription: "A comprehensive health and safety qualification covering essential workplace safety principles, risk assessment methodologies, and legal responsibilities. This course ensures learners understand their duties under health and safety legislation and can contribute to a safe working environment.",
        topics: ["Workplace safety", "Risk assessment", "Emergency procedures", "Legal responsibilities"],
        modules: [
          {
            id: "hs-module-1",
            title: "Workplace Hazards & Risks",
            description: "Identify and understand common workplace hazards",
            learningOutcomes: [
              "Identify different types of workplace hazards",
              "Understand the principles of risk assessment",
              "Recognise potential dangers in various work environments",
              "Apply the hierarchy of control measures"
            ],
            topics: ["Physical hazards", "Chemical hazards", "Biological hazards", "Ergonomic risks", "Psychosocial factors"],
            assessmentCriteria: ["Complete hazard identification exercises", "Conduct a basic risk assessment"]
          },
          {
            id: "hs-module-2",
            title: "Risk Assessment",
            description: "Learn systematic approaches to assessing and managing workplace risks",
            learningOutcomes: [
              "Conduct a systematic risk assessment",
              "Evaluate risk levels using appropriate matrices",
              "Develop control measures and safe systems of work",
              "Document and review risk assessments"
            ],
            topics: ["5 steps to risk assessment", "Risk matrices", "Control measures", "Documentation requirements", "Review procedures"],
            assessmentCriteria: ["Complete a workplace risk assessment", "Create a method statement"]
          },
          {
            id: "hs-module-3",
            title: "Emergency Procedures",
            description: "Understand emergency response protocols and procedures",
            learningOutcomes: [
              "Understand fire safety procedures and evacuation protocols",
              "Know how to respond to various emergency situations",
              "Understand first aid requirements and responsibilities",
              "Communicate effectively during emergencies"
            ],
            topics: ["Fire safety", "Evacuation procedures", "First aid basics", "Emergency contacts", "Incident reporting"],
            assessmentCriteria: ["Demonstrate understanding of evacuation procedures", "Complete emergency scenario exercises"]
          }
        ],
        whoThisCourseIsFor: [
          "New employees requiring health and safety induction",
          "Managers and supervisors with health and safety responsibilities",
          "Those working in high-risk environments",
          "Anyone needing certification for employment"
        ],
        entryRequirements: [
          "Age 16+",
          "No prior qualifications required",
          "Suitable for all industries and sectors"
        ],
        careerPaths: [
          { title: "All Sectors", roles: "Required for most employment positions" },
          { title: "Management", roles: "Health and safety officer, facilities manager" }
        ],
        courseBenefits: [
          "Nationally recognised Level 2 certificate",
          "Meets employer legal requirements",
          "Reduces workplace accident risk",
          "Essential for employment in many sectors"
        ],
        employmentOutcomes: "Applicable across all sectors",
        accreditation: "Nationally recognised Level 2 qualification"
      },
      {
        id: "food-hygiene",
        name: "Food & Hygiene Training",
        level: "Level 2",
        duration: "1 day",
        shortDescription: "Food safety certification for catering and food handling",
        fullDescription: "Essential food safety training for anyone working with food. Covers legal requirements, hazard analysis, personal hygiene standards, and safe food handling practices. Upon completion, learners receive a nationally recognised certificate valid for 3 years, meeting Food Standards Agency requirements.",
        topics: ["Food safety regulations", "Cross-contamination prevention", "Personal hygiene", "Safe food storage"],
        modules: [
          {
            id: "fh-module-1",
            title: "Food Safety Law & Responsibilities",
            description: "Understand legal obligations and food safety management systems",
            learningOutcomes: [
              "Understand key food safety legislation",
              "Explain the principles of HACCP",
              "Identify food safety responsibilities for different roles",
              "Recognise the role of Environmental Health Officers"
            ],
            topics: ["Food Safety Act 1990", "HACCP principles", "Due diligence", "Food premises registration", "Inspection procedures"],
            assessmentCriteria: ["Complete multiple choice assessment", "Identify legal requirements for food businesses"]
          },
          {
            id: "fh-module-2",
            title: "Contamination & Prevention",
            description: "Learn about contamination risks and how to prevent them",
            learningOutcomes: [
              "Identify different types of contamination",
              "Understand cross-contamination risks",
              "Implement effective prevention measures",
              "Manage allergens safely"
            ],
            topics: ["Physical contamination", "Chemical contamination", "Biological hazards", "Cross-contamination", "Allergen management", "Temperature danger zone"],
            assessmentCriteria: ["Identify contamination risks in scenarios", "Demonstrate understanding of prevention methods"]
          },
          {
            id: "fh-module-3",
            title: "Personal Hygiene & Food Storage",
            description: "Best practices for personal hygiene and safe food storage",
            learningOutcomes: [
              "Maintain appropriate personal hygiene standards",
              "Understand handwashing best practices",
              "Apply correct food storage procedures",
              "Implement effective cleaning schedules"
            ],
            topics: ["Personal cleanliness", "Protective clothing", "Handwashing techniques", "Refrigeration and freezing", "Stock rotation", "Cleaning and sanitising"],
            assessmentCriteria: ["Demonstrate proper handwashing technique", "Complete food storage and temperature assessment"]
          }
        ],
        whoThisCourseIsFor: [
          "Food handlers in catering, retail, and manufacturing",
          "Kitchen staff and chefs",
          "Those working in cafes, restaurants, and food retail",
          "Volunteers handling food at events"
        ],
        entryRequirements: [
          "Age 16+",
          "No prior qualifications required",
          "Suitable for beginners and experienced food handlers"
        ],
        careerPaths: [
          { title: "Catering & Hospitality", roles: "Chef, kitchen assistant, catering assistant, waiter/waitress" },
          { title: "Food Retail", roles: "Shop assistant, deli counter staff, bakery assistant" },
          { title: "Care Sector", roles: "Care home kitchen staff, school catering" }
        ],
        courseBenefits: [
          "Nationally recognised certificate valid for 3 years",
          "Meets Food Standards Agency requirements",
          "Essential for employment in food industry",
          "Improves employability in hospitality sector"
        ],
        employmentOutcomes: "Catering, hospitality, food retail",
        accreditation: "Nationally recognised Level 2 Food Hygiene certificate"
      },
      {
        id: "safeguarding",
        name: "Safeguarding",
        level: "Level 2",
        duration: "1 day",
        shortDescription: "Protecting children and vulnerable adults from harm",
        fullDescription: "Comprehensive safeguarding training covering the protection of children, young people, and vulnerable adults. Learners will understand how to recognise signs of abuse and neglect, know how to respond to concerns, and understand their legal responsibilities. Essential for anyone working with children or vulnerable adults.",
        topics: ["Identifying abuse and neglect", "Reporting procedures", "Child and adult protection", "Legal frameworks"],
        modules: [
          {
            id: "sg-module-1",
            title: "Understanding Abuse & Neglect",
            description: "Recognise different types of abuse and their indicators",
            learningOutcomes: [
              "Identify different types of abuse",
              "Recognise signs and indicators of abuse",
              "Understand factors that increase vulnerability",
              "Distinguish between accidental and non-accidental injuries"
            ],
            topics: ["Physical abuse", "Emotional abuse", "Sexual abuse", "Neglect", "Domestic abuse", "Modern slavery", "Self-neglect"],
            assessmentCriteria: ["Identify abuse types from case studies", "Recognise warning signs and indicators"]
          },
          {
            id: "sg-module-2",
            title: "Reporting & Procedures",
            description: "Know how to respond to and report safeguarding concerns",
            learningOutcomes: [
              "Understand disclosure procedures",
              "Know how to respond to disclosures appropriately",
              "Follow correct reporting channels",
              "Document concerns accurately"
            ],
            topics: ["Responding to disclosure", "Recording concerns", "Reporting procedures", "Whistleblowing", "Confidentiality", "Information sharing"],
            assessmentCriteria: ["Demonstrate responding to a disclosure", "Complete a safeguarding concern form"]
          },
          {
            id: "sg-module-3",
            title: "Legal Framework & Responsibilities",
            description: "Understand the legal basis for safeguarding and your duties",
            learningOutcomes: [
              "Understand key safeguarding legislation",
              "Know the role of different agencies",
              "Understand the concept of 'duty of care'",
              "Apply the principles of the Mental Capacity Act"
            ],
            topics: ["Working Together to Safeguard Children", "Care Act 2014", "Mental Capacity Act", "Multi-agency working", "Local Safeguarding Partnerships", "Designated safeguarding lead responsibilities"],
            assessmentCriteria: ["Demonstrate understanding of legal framework", "Complete assessment on roles and responsibilities"]
          }
        ],
        whoThisCourseIsFor: [
          "Those working with children or young people",
          "Healthcare and social care workers",
          "Teachers, teaching assistants, and nursery staff",
          "Volunteers in community organisations",
          "Anyone with a duty of care to vulnerable people"
        ],
        entryRequirements: [
          "Age 16+",
          "No prior qualifications required",
          "Suitable for all sectors working with children or vulnerable adults"
        ],
        careerPaths: [
          { title: "Education", roles: "Teaching assistant, nursery nurse, learning support" },
          { title: "Healthcare", roles: "Care assistant, support worker, healthcare assistant" },
          { title: "Community Services", roles: "Youth worker, community support, volunteer coordinator" }
        ],
        courseBenefits: [
          "Nationally recognised Level 2 certificate",
          "Meets employer safeguarding requirements",
          "Required for DBS-checked positions",
          "Valid for 3 years"
        ],
        employmentOutcomes: "Education, care, community services",
        accreditation: "Nationally recognised Level 2 Safeguarding certificate"
      },
      {
        id: "paediatric-first-aid",
        name: "Paediatric First Aid",
        level: "Level 3",
        duration: "2 days",
        shortDescription: "Emergency first aid for infants and children",
        fullDescription: "A comprehensive 2-day paediatric first aid course covering emergency response for infants and children. Meets Ofsted requirements and Early Years Foundation Stage (EYFS) guidelines. Essential for those working with children, including childminders, nursery staff, and teachers. Blends theory with extensive practical scenarios.",
        topics: ["Emergency life support for infants/children", "Managing illness and injury", "First aid regulations"],
        modules: [
          {
            id: "pfa-module-1",
            title: "Emergency Life Support",
            description: "Essential life-saving skills for infants and children",
            learningOutcomes: [
              "Perform CPR on infants and children",
              "Manage choking incidents safely",
              "Place a child in the recovery position",
              "Recognise and respond to anaphylaxis"
            ],
            topics: ["Infant CPR", "Child CPR", "Choking management", "Recovery position", "Anaphylaxis and EpiPen use", "Resuscitation guidelines"],
            assessmentCriteria: ["Demonstrate CPR on infant and child manikins", "Demonstrate choking treatment", "Pass practical assessment"]
          },
          {
            id: "pfa-module-2",
            title: "Common Illnesses & Injuries",
            description: "Managing common childhood emergencies and illnesses",
            learningOutcomes: [
              "Treat common childhood injuries",
              "Recognise serious illness requiring emergency care",
              "Manage bleeding and wounds appropriately",
              "Respond to suspected fractures and head injuries"
            ],
            topics: ["Wounds and bleeding", "Burns and scalds", "Fractures and dislocations", "Head injuries", "Seizures and febrile convulsions", "Meningitis signs", "Asthma attacks"],
            assessmentCriteria: ["Demonstrate wound treatment", "Complete illness recognition assessment"]
          },
          {
            id: "pfa-module-3",
            title: "First Aid Regulations & Record Keeping",
            description: "Understanding your responsibilities as a first aider",
            learningOutcomes: [
              "Understand first aid regulations and requirements",
              "Maintain accurate first aid records",
              "Know when to call emergency services",
              "Maintain first aid equipment and supplies"
            ],
            topics: ["First aid regulations", "Record keeping and reporting", "RIDDOR requirements", "First aid kit contents", "Accident reporting", "Parent communication"],
            assessmentCriteria: ["Complete incident report form", "Demonstrate understanding of regulatory requirements"]
          }
        ],
        whoThisCourseIsFor: [
          "Childminders and nursery staff",
          "Teachers and teaching assistants",
          "Parents and carers",
          "Anyone working with children under 8 years",
          "Sports coaches and youth workers"
        ],
        entryRequirements: [
          "Age 16+",
          "No prior first aid experience required",
          "Physical ability to perform CPR on floor-level manikins"
        ],
        careerPaths: [
          { title: "Childcare", roles: "Nursery nurse, childminder, teaching assistant" },
          { title: "Education", roles: "Primary teacher, SEN support, classroom assistant" },
          { title: "Sports & Leisure", roles: "Swimming instructor, sports coach, activity leader" }
        ],
        courseBenefits: [
          "Meets Ofsted and EYFS requirements",
          "Certificate valid for 3 years",
          "Essential for childcare registration",
          "Practical, hands-on training"
        ],
        employmentOutcomes: "Childcare, nurseries, schools, childminding",
        accreditation: "Ofsted-compliant Level 3 Paediatric First Aid certificate"
      },
      {
        id: "business-admin",
        name: "Business & Administration",
        level: "Level 2",
        duration: "16 weeks",
        shortDescription: "Essential office and administrative skills for modern workplaces",
        fullDescription: "A comprehensive qualification covering essential administrative and business skills. Learners develop practical abilities in office procedures, digital tools, professional communication, and organisational skills. Ideal for those seeking administrative roles or looking to formalise existing experience with a recognised qualification.",
        topics: ["Office procedures", "Communication skills", "Document production", "Digital tools"],
        modules: [
          {
            id: "ba-module-1",
            title: "Administrative Procedures",
            description: "Understand and apply effective office procedures and systems",
            learningOutcomes: [
              "Manage office systems and procedures effectively",
              "Organise and maintain filing systems",
              "Handle incoming and outgoing communications",
              "Coordinate meetings and appointments"
            ],
            topics: ["Office organisation", "Filing systems", "Meeting management", "Diary management", "Travel arrangements", "Supplier management"],
            assessmentCriteria: ["Demonstrate effective filing system management", "Coordinate a simulated meeting"]
          },
          {
            id: "ba-module-2",
            title: "Professional Communication",
            description: "Develop effective written and verbal communication skills",
            learningOutcomes: [
              "Write professional business communications",
              "Handle telephone calls professionally",
              "Compose appropriate emails and letters",
              "Adapt communication style for different audiences"
            ],
            topics: ["Business writing", "Email etiquette", "Telephone techniques", "Meeting minutes", "Report writing", "Customer correspondence"],
            assessmentCriteria: ["Produce sample business communications", "Demonstrate telephone handling"]
          },
          {
            id: "ba-module-3",
            title: "Digital Tools & IT Skills",
            description: "Use office software and digital tools effectively",
            learningOutcomes: [
              "Use word processing software confidently",
              "Create and manage spreadsheets",
              "Prepare professional presentations",
              "Navigate office IT systems securely"
            ],
            topics: ["Microsoft Word", "Microsoft Excel", "Microsoft PowerPoint", "Email systems", "Data security", "Cloud storage"],
            assessmentCriteria: ["Produce formatted business documents", "Create functional spreadsheet", "Deliver a presentation"]
          },
          {
            id: "ba-module-4",
            title: "Customer Service in Administration",
            description: "Provide excellent service to internal and external customers",
            learningOutcomes: [
              "Handle customer enquiries professionally",
              "Manage expectations and deliver information clearly",
              "Resolve administrative issues efficiently",
              "Maintain positive working relationships"
            ],
            topics: ["Internal customer service", "Visitor management", "Complaint handling", "Professional image", "Team communication", "Stakeholder management"],
            assessmentCriteria: ["Demonstrate customer interaction scenarios", "Handle simulated complaint situation"]
          }
        ],
        whoThisCourseIsFor: [
          "Those seeking administrative or office-based roles",
          "Career changers moving into office work",
          "Existing admin staff seeking formal qualification",
          "School leavers entering the workforce"
        ],
        entryRequirements: [
          "Age 19+ for funded places",
          "No formal qualifications required",
          "Basic IT literacy helpful but not essential",
          "Good standard of written English"
        ],
        careerPaths: [
          { title: "Administration", roles: "Administrative assistant, office assistant, receptionist, secretary" },
          { title: "Business Support", roles: "Personal assistant, executive assistant, office manager" },
          { title: "Public Sector", roles: "Council admin, NHS administrator, government office support" }
        ],
        courseBenefits: [
          "Nationally recognised Level 2 qualification",
          "Practical, job-ready skills",
          "Enhanced employability across sectors",
          "Foundation for further study"
        ],
        employmentOutcomes: "Admin roles, receptionist, office assistant",
        accreditation: "Nationally recognised Level 2 qualification - NCFE/City & Guilds equivalent"
      }
    ],
    enrollmentInfo: {
      cost: "Varies - funding available for eligible learners",
      schedule: "Flexible scheduling available",
      nextIntake: "Rolling admissions",
      support: "One-to-one mentoring and job placement assistance"
    }
  },
  {
    id: "life-skills",
    title: "Life Skills Training",
    shortDescription: "Essential literacy, numeracy, and digital skills",
    description: "Practical skills that help you integrate into society, manage daily tasks, support your children's education, and prepare for employment.",
    category: "essential-skills",
    whoThisHelps: [
      "Asylum seekers and refugees",
      "New arrivals to the UK",
      "Non-native English speakers",
      "Adults returning to education"
    ],
    programs: [
      {
        id: "esol",
        name: "ESOL",
        level: "Entry 1 to Level 2",
        duration: "12-16 weeks",
        shortDescription: "English for Speakers of Other Languages",
        fullDescription: "English language courses for speakers of other languages, from complete beginners to intermediate level. Develop speaking, listening, reading, and writing skills for everyday life, work, and further education. Classes are welcoming and supportive, with small group sizes and experienced tutors.",
        topics: ["Basic to advanced English communication", "Everyday vocabulary", "Listening and speaking skills"],
        modules: [
          {
            id: "esol-module-1",
            title: "Speaking & Listening",
            description: "Develop confidence in spoken English for everyday situations",
            learningOutcomes: [
              "Engage in everyday conversations",
              "Ask and answer questions confidently",
              "Express opinions and needs clearly",
              "Understand spoken English in various contexts"
            ],
            topics: ["Greetings and introductions", "Shopping and services", "Health and wellbeing", "Directions and travel", "Telephone conversations", "Social interactions"],
            assessmentCriteria: ["Participate in speaking assessments", "Complete listening comprehension tasks"]
          },
          {
            id: "esol-module-2",
            title: "Reading Skills",
            description: "Read and understand everyday texts and documents",
            learningOutcomes: [
              "Read and understand everyday signs and notices",
              "Understand simple texts and instructions",
              "Extract information from forms and documents",
              "Use reading strategies for unfamiliar words"
            ],
            topics: ["Signs and labels", "Forms and documents", "Simple narratives", "Instructions and directions", "Letters and emails", "News and information texts"],
            assessmentCriteria: ["Complete reading comprehension tasks", "Demonstrate understanding of everyday texts"]
          },
          {
            id: "esol-module-3",
            title: "Writing Skills",
            description: "Write clearly and accurately for practical purposes",
            learningOutcomes: [
              "Write simple sentences and short texts",
              "Complete forms accurately",
              "Write informal and formal correspondence",
              "Use correct spelling and punctuation"
            ],
            topics: ["Sentence structure", "Paragraphs and text organisation", "Spelling strategies", "Punctuation basics", "Form filling", "Email and letter writing"],
            assessmentCriteria: ["Complete writing tasks at appropriate level", "Write short texts for practical purposes"]
          },
          {
            id: "esol-module-4",
            title: "English for Work & Life",
            description: "Practical English skills for employment and daily life",
            learningOutcomes: [
              "Communicate effectively in workplace situations",
              "Understand workplace vocabulary and instructions",
              "Participate in job applications and interviews",
              "Access services and support in the community"
            ],
            topics: ["Workplace vocabulary", "Job applications and CVs", "Interview skills", "Accessing services", "Parent-teacher communication", "Medical appointments"],
            assessmentCriteria: ["Demonstrate workplace communication", "Complete simulated job application tasks"]
          }
        ],
        whoThisCourseIsFor: [
          "Speakers of other languages learning English",
          "New arrivals to the UK",
          "Asylum seekers and refugees",
          "Those wanting to improve English for work or study"
        ],
        entryRequirements: [
          "Initial assessment to determine level",
          "No prior English qualifications required",
          "Commitment to attend classes regularly"
        ],
        careerPaths: [
          { title: "Further Education", roles: "Progress to Functional Skills, vocational courses" },
          { title: "Employment", roles: "Entry-level roles across all sectors" },
          { title: "Community Integration", roles: "Access services, support children's education" }
        ],
        courseBenefits: [
          "Small, supportive class groups",
          "Experienced ESOL tutors",
          "Flexible daytime and evening classes",
          "Free for eligible learners"
        ],
        accreditation: "Nationally recognised ESOL qualification - ESB/City & Guilds"
      },
      {
        id: "functional-skills-english",
        name: "Functional Skills English",
        level: "Entry Level to Level 2",
        duration: "20-24 weeks",
        shortDescription: "Reading, writing and communication skills for everyday life and work",
        fullDescription: "Develop essential English skills for everyday life, work, and further education. Covers reading, writing, speaking, and listening at your level. Functional Skills qualifications are equivalent to GCSEs and are recognised by employers and colleges. Ideal for those who want to improve their English without taking a traditional GCSE course.",
        topics: ["Reading and understanding texts", "Written communication", "Speaking and listening"],
        modules: [
          {
            id: "fse-module-1",
            title: "Reading",
            description: "Read and understand a range of texts for different purposes",
            learningOutcomes: [
              "Identify main points and ideas in texts",
              "Understand and use information from documents",
              "Read and respond to different text types",
              "Use reference materials and research skills"
            ],
            topics: ["Reading comprehension", "Information retrieval", "Text purpose and audience", "Inference and deduction", "Fact vs opinion", "Comparing texts"],
            assessmentCriteria: ["Complete reading assessment", "Demonstrate understanding of varied texts"]
          },
          {
            id: "fse-module-2",
            title: "Writing",
            description: "Write clearly and effectively for different audiences and purposes",
            learningOutcomes: [
              "Plan and organise writing effectively",
              "Use appropriate format and style",
              "Write with accurate grammar, spelling, and punctuation",
              "Edit and improve own writing"
            ],
            topics: ["Planning and drafting", "Text organisation", "Grammar and sentence structure", "Spelling strategies", "Punctuation", "Writing for different purposes"],
            assessmentCriteria: ["Complete writing assessment", "Produce varied written work"]
          },
          {
            id: "fse-module-3",
            title: "Speaking, Listening & Communication",
            description: "Communicate effectively in discussions and presentations",
            learningOutcomes: [
              "Participate effectively in discussions",
              "Present information clearly to others",
              "Listen and respond appropriately",
              "Adapt communication for different situations"
            ],
            topics: ["Discussion skills", "Presentation techniques", "Active listening", "Responding to questions", "Non-verbal communication", "Persuasion and negotiation"],
            assessmentCriteria: ["Participate in group discussion", "Deliver short presentation"]
          }
        ],
        whoThisCourseIsFor: [
          "Adults wanting to improve English skills",
          "Those needing English qualification for work or study",
          "People who didn't achieve GCSE English",
          "Those wanting to support children's learning"
        ],
        entryRequirements: [
          "Initial assessment to determine level",
          "Commitment to attend sessions regularly",
          "Suitable for adults aged 19+"
        ],
        careerPaths: [
          { title: "Further Study", roles: "Access to college courses, apprenticeships, university" },
          { title: "Employment", roles: "Improved prospects across all job sectors" },
          { title: "Teaching & Care", roles: "Progress to teaching assistant, care qualifications" }
        ],
        courseBenefits: [
          "GCSE-equivalent qualification",
          "Accepted by employers and colleges",
          "Flexible study options",
          "Builds confidence in English skills"
        ],
        accreditation: "Nationally recognised Functional Skills English qualification"
      },
      {
        id: "functional-skills-maths",
        name: "Functional Skills Maths",
        level: "Entry Level to Level 2",
        duration: "20-24 weeks",
        shortDescription: "Practical numeracy for everyday life and work",
        fullDescription: "Develop practical maths skills for everyday situations and work. Learn to use numbers confidently, work with fractions and percentages, measure accurately, and interpret data. Functional Skills Maths qualifications are equivalent to GCSEs and accepted by employers and colleges as an alternative to traditional maths qualifications.",
        topics: ["Everyday numeracy", "Financial calculations", "Problem-solving"],
        modules: [
          {
            id: "fsm-module-1",
            title: "Using Numbers",
            description: "Work confidently with whole numbers, fractions, decimals, and percentages",
            learningOutcomes: [
              "Perform calculations with whole numbers",
              "Work with fractions, decimals, and percentages",
              "Solve real-life number problems",
              "Check answers using estimation and inverse operations"
            ],
            topics: ["Addition, subtraction, multiplication, division", "Fractions and decimals", "Percentages and discounts", "Ratio and proportion", "Negative numbers", "Calculator and non-calculator methods"],
            assessmentCriteria: ["Complete number calculations assessment", "Solve practical number problems"]
          },
          {
            id: "fsm-module-2",
            title: "Measure, Shape & Space",
            description: "Use measuring skills and understand shape and space",
            learningOutcomes: [
              "Calculate with money accurately",
              "Work with time and timetables",
              "Calculate perimeter, area, and volume",
              "Use scales and convert between units"
            ],
            topics: ["Money calculations", "Time and duration", "Length, weight, capacity", "Perimeter and area", "Volume", "Scale drawings and maps", "Unit conversions"],
            assessmentCriteria: ["Complete measurement tasks", "Solve practical measuring problems"]
          },
          {
            id: "fsm-module-3",
            title: "Handling Data",
            description: "Collect, organise, and interpret data",
            learningOutcomes: [
              "Collect and organise data effectively",
              "Present data using tables and charts",
              "Calculate and interpret averages",
              "Understand probability basics"
            ],
            topics: ["Data collection", "Tables and charts", "Bar charts and pie charts", "Mean, median, mode, range", "Interpreting graphs and charts", "Probability basics"],
            assessmentCriteria: ["Complete data handling tasks", "Interpret and present data"]
          },
          {
            id: "fsm-module-4",
            title: "Maths for Work & Life",
            description: "Apply maths skills to practical real-world situations",
            learningOutcomes: [
              "Budget and manage personal finances",
              "Compare deals and understand interest",
              "Use maths in workplace scenarios",
              "Solve everyday mathematical problems"
            ],
            topics: ["Budgeting", "Best buys and value for money", "Payslips and tax", "Interest and loans", "DIY calculations", "Travel planning"],
            assessmentCriteria: ["Complete practical maths scenarios", "Apply skills to real-life contexts"]
          }
        ],
        whoThisCourseIsFor: [
          "Adults wanting to improve maths skills",
          "Those needing maths qualification for work or study",
          "People who didn't achieve GCSE Maths",
          "Those wanting to support children's learning"
        ],
        entryRequirements: [
          "Initial assessment to determine level",
          "Commitment to attend sessions regularly",
          "Suitable for adults aged 19+"
        ],
        careerPaths: [
          { title: "Further Study", roles: "Access to college courses, apprenticeships, university" },
          { title: "Employment", roles: "Improved prospects across all job sectors" },
          { title: "Skilled Trades", roles: "Progress to construction, engineering, accounting support" }
        ],
        courseBenefits: [
          "GCSE-equivalent qualification",
          "Accepted by employers and colleges",
          "Practical, real-world focus",
          "Builds confidence with numbers"
        ],
        accreditation: "Nationally recognised Functional Skills Maths qualification"
      },
      {
        id: "it-digital-skills",
        name: "IT & Digital Skills",
        level: "Beginner to Intermediate",
        duration: "8-12 weeks",
        shortDescription: "Computer basics and digital confidence for everyday life",
        fullDescription: "Build confidence using computers, the internet, and digital devices. Learn essential IT skills for everyday life, work, and staying connected. This course is designed for beginners or those wanting to update their digital skills. Covers everything from basic computer use to online safety and popular applications.",
        topics: ["Basic computer use", "Internet navigation and safety", "Using word processors and spreadsheets"],
        modules: [
          {
            id: "it-module-1",
            title: "Computer Basics",
            description: "Learn fundamental computer skills and navigation",
            learningOutcomes: [
              "Turn on, log in, and navigate a computer confidently",
              "Create, save, and organise files and folders",
              "Understand basic computer terminology",
              "Troubleshoot common simple issues"
            ],
            topics: ["Turning on and logging in", "Desktop and navigation", "Files and folders", "Mouse and keyboard skills", "Common terminology", "Basic troubleshooting"],
            assessmentCriteria: ["Demonstrate basic computer tasks", "Create and organise files"]
          },
          {
            id: "it-module-2",
            title: "Internet & Email",
            description: "Navigate the internet safely and use email effectively",
            learningOutcomes: [
              "Browse the internet confidently and safely",
              "Search for information effectively",
              "Send, receive, and organise emails",
              "Stay safe online and protect personal information"
            ],
            topics: ["Web browsers and navigation", "Effective searching", "Online safety basics", "Email setup and use", "Attachments", "Spotting scams and phishing"],
            assessmentCriteria: ["Complete internet search tasks", "Send and receive emails with attachments"]
          },
          {
            id: "it-module-3",
            title: "Essential Applications",
            description: "Use common software for everyday tasks",
            learningOutcomes: [
              "Create and format documents using word processing",
              "Create simple spreadsheets for calculations",
              "Use video calling and messaging apps",
              "Access online services and forms"
            ],
            topics: ["Microsoft Word basics", "Microsoft Excel basics", "Video calling (Zoom, Teams)", "Messaging apps", "Online forms", "Banking and government services online"],
            assessmentCriteria: ["Create a formatted document", "Build a simple spreadsheet", "Complete online form"]
          }
        ],
        whoThisCourseIsFor: [
          "Complete beginners wanting to learn computer skills",
          "Those wanting to update digital skills",
          "Job seekers needing IT confidence",
          "Anyone wanting to stay safe online"
        ],
        entryRequirements: [
          "No prior computer experience needed",
          "Suitable for complete beginners",
          "All ages welcome"
        ],
        careerPaths: [
          { title: "Office Work", roles: "Administrative roles, reception, data entry" },
          { title: "Digital Confidence", roles: "Access online services, stay connected with family" },
          { title: "Further IT", roles: "Progress to advanced IT qualifications" }
        ],
        courseBenefits: [
          "Patient, supportive teaching",
          "Hands-on practice",
          "Build confidence with technology",
          "Essential for modern life and work"
        ],
        accreditation: "Nationally recognised IT User qualification"
      }
    ],
    successStory: {
      name: "Amira",
      background: "Syrian refugee",
      journey: "Arrived with no English. Completed ESOL and IT skills courses. Now volunteers helping others.",
      quote: "APT gave me the confidence to be part of my community and the skills to help my children with homework."
    },
    enrollmentInfo: {
      cost: "Free for eligible participants",
      schedule: "Morning, afternoon, and evening options",
      childcare: "Creche available for select classes",
      nextIntake: "September, January, April"
    }
  }
];

export const PersonalTrainingCategories = [
  {
    id: "professional-training",
    title: "Professional Training",
    url: "/training/personal/professional-training",
  },
  {
    id: "life-skills",
    title: "Life Skills Training",
    url: "/training/personal/life-skills",
  }
];


// ==========================================
// 2. BUSINESS TRAINING DATA
// ==========================================
export const BusinessTrainingData = [
  {
    id: "healthcare-package",
    title: "Healthcare Training Package",
    shortDescription: "Comprehensive 15-unit healthcare certification",
    description: "A tailored, bespoke training package designed for healthcare providers looking to upskill their staff. Covers 15 essential units to ensure full compliance with CQC requirements and the highest standards of care.",
    category: "business-package",
    icon: "healthcare",
    outcomes: [
      "Meet CQC compliance requirements with confidence",
      "Reduce staff turnover through meaningful career investment",
      "Ensure patient safety and quality-of-care standards",
    ],
    features: [
      "15 comprehensive units covering all core health & social care areas",
      "Fully accredited healthcare certificate upon completion",
      "On-site or flexible delivery options",
      "Tailored to your organisation's specific needs and shift patterns",
    ],
    duration: "4–8 weeks (flexible)",
    minGroupSize: 5,
    bookingInfo: "Contact us for bulk enrolment and tailored delivery plans.",
  },
  {
    id: "childcare-package",
    title: "Childcare Training Package",
    shortDescription: "Complete certification for early years settings",
    description: "Designed for nurseries, schools, and local authorities. Ensures your staff are fully trained in safeguarding, paediatric first aid, and early years development — keeping you Ofsted-ready at all times.",
    category: "business-package",
    icon: "childcare",
    outcomes: [
      "Achieve and maintain Ofsted compliance",
      "Build safeguarding confidence across your whole team",
      "Improve the quality and safety of your early years provision",
    ],
    features: [
      "Includes Paediatric First Aid (2-day practical)",
      "Safeguarding children and vulnerable adults",
      "Accredited Childcare Certificate",
      "Flexible group delivery — we come to you",
    ],
    duration: "2–4 weeks (flexible)",
    minGroupSize: 5,
    bookingInfo: "Contact us for bulk enrolment and tailored delivery plans.",
  },
  {
    id: "admin-package",
    title: "Admin Sector Training Package",
    shortDescription: "Upskill your office and support staff",
    description: "Practical, intensive training for your administrative and office teams. Covers modern procedures, digital tools, communication, and customer service — producing sharper, more efficient support staff.",
    category: "business-package",
    icon: "admin",
    outcomes: [
      "Improve team productivity and efficiency",
      "Modernise digital skills across your workforce",
      "Build professional communication and customer service",
    ],
    features: [
      "Business & Administration Level 2/3 equivalent modules",
      "Accredited Administration Certificate",
      "Practical IT, Word, Excel, and email skills",
      "Customer service and professional communication",
    ],
    duration: "6–10 weeks (flexible)",
    minGroupSize: 4,
    bookingInfo: "Contact us for bulk enrolment and tailored delivery plans.",
  },
  {
    id: "catering-package",
    title: "Catering Sector Training Package",
    shortDescription: "Essential compliance for food services",
    description: "Ensure every member of your catering team meets legal requirements for food preparation, hygiene, allergen awareness, and workplace safety. Fast-track options available for urgent compliance needs.",
    category: "business-package",
    icon: "catering",
    outcomes: [
      "Achieve full food safety regulatory compliance",
      "Protect your business from enforcement risk",
      "Build a culture of hygiene and food safety",
    ],
    features: [
      "Food & Hygiene Level 2 (nationally accredited)",
      "Allergen awareness training",
      "General Health & Safety",
      "Accredited Catering Sector Certificate",
      "Fast-track delivery for urgent compliance needs",
    ],
    duration: "1–3 weeks (fast-track available)",
    minGroupSize: 4,
    bookingInfo: "Contact us for bulk enrolment and tailored delivery plans.",
  },
];

export const BusinessTrainingCategories = [
  {
    id: "healthcare-package",
    title: "Healthcare Package",
    url: "/training/business/healthcare-package",
  },
  {
    id: "childcare-package",
    title: "Childcare Package",
    url: "/training/business/childcare-package",
  },
  {
    id: "admin-package",
    title: "Admin Sector Package",
    url: "/training/business/admin-package",
  },
  {
    id: "catering-package",
    title: "Catering Sector Package",
    url: "/training/business/catering-package",
  },
];

// ==========================================
// 3. BUSINESS TESTIMONIALS
// ==========================================
export const BusinessTestimonials = [
  {
    id: 1,
    quote:
      "APT provided our staff with bespoke compliance training. The process was seamless, engaging, and highly professional. Our CQC rating improved as a direct result.",
    author: "Mark Dawson",
    role: "Operations Manager",
    organisation: "East London Care Home",
    sector: "healthcare",
  },
  {
    id: 2,
    quote:
      "We needed to get our nursery Ofsted-compliant quickly before an inspection. APT turned it around in two weeks. Every team member was trained, certified, and confident.",
    author: "Sarah Thompson",
    role: "Nursery Manager",
    organisation: "Little Stars Nursery",
    sector: "childcare",
  },
  {
    id: 3,
    quote:
      "Our kitchen team went from zero to fully Food Hygiene Level 2 certified in just a week. APT are professional, friendly, and genuinely care about outcomes.",
    author: "Yusuf Ali",
    role: "Head Chef & Owner",
    organisation: "Spice Route Restaurant",
    sector: "catering",
  },
];

// ==========================================
// 4. BUSINESS FAQ
// ==========================================
export const BusinessFAQ = [
  {
    question: "What is the minimum group size for training?",
    answer:
      "We can train groups as small as 4–5 participants. For smaller teams, we sometimes combine groups or offer individual training solutions. Contact us to discuss your specific situation.",
  },
  {
    question: "Can you train at our premises?",
    answer:
      "Yes — on-site delivery is one of our most popular options. We come to you, which minimises disruption and means your team trains in their own environment. We can also deliver remotely or at our East London training centre.",
  },
  {
    question: "Is funding available for businesses?",
    answer:
      "Some funding options may be available depending on your organisation type, sector, and the courses involved. We help eligible organisations navigate funding routes to reduce or eliminate training costs. Get in touch and we'll assess what's available to you.",
  },
  {
    question: "How long does certification take?",
    answer:
      "It depends on the package. Short compliance courses (e.g., Food Hygiene, Health & Safety) can be completed in 1–3 days. Full sector packages (e.g., Healthcare 15-unit) typically run over 4–8 weeks with flexible delivery. We always tailor the timeline to fit your operations.",
  },
  {
    question: "Are your certifications nationally recognised?",
    answer:
      "Yes. All our qualifications are nationally accredited and recognised by relevant regulatory bodies including CQC, Ofsted, and Food Standards regulators. We only deliver certifications that hold real professional value.",
  },
  {
    question: "Do you offer ongoing support after training?",
    answer:
      "Absolutely. Your dedicated account manager stays in contact after certification, supports staff who need refreshers, and helps you plan follow-on training as your team grows. We build long-term partnerships, not one-off transactions.",
  },
  {
    question: "Can we customise a package that isn't listed?",
    answer:
      "Yes — and we encourage it. Many of our clients have unique needs that don't fit neatly into a standard package. We design fully bespoke programmes from scratch. Just contact us for a free consultation.",
  },
];

// For fallback or aggregated uses (like the dynamic TrainingPage component if it needs everything)
export const TrainingData = [...PersonalTrainingData, ...BusinessTrainingData];
